import React, { useState, useEffect } from 'react';
import { router, usePage } from '@inertiajs/react';
import { X, Save, Search } from 'lucide-react';
import { toast } from 'react-toastify';

const ScriptSelectionModal = ({ isOpen, onClose }) => {
  const { scripts, urls } = usePage().props;
  const [loading, setLoading] = useState(false);
  const [selectedScripts, setSelectedScripts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);

  const availableScripts = scripts ? scripts.filter(script => 
    !script.has_profile || !script.is_public
  ) : [];

  useEffect(() => {
    if (isOpen) {
      setSelectedScripts([]);
      setSearchTerm('');
      setAcknowledged(false); // Reset acknowledgment when modal opens
    }
  }, [isOpen]);

  const filteredScripts = availableScripts.filter(script =>
    script.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleScriptSelection = (scriptHash) => {
    if (selectedScripts.includes(scriptHash)) {
      setSelectedScripts(selectedScripts.filter(hash => hash !== scriptHash));
    } else {
      setSelectedScripts([...selectedScripts, scriptHash]);
    }
  };

  const addSelectedScripts = async () => {
    if (selectedScripts.length === 0) return;
    
    setLoading(true);
    let successCount = 0;
    let errorCount = 0;

    // Process each script sequentially to avoid conflicts
    for (const scriptHash of selectedScripts) {
      const script = scripts.find(s => s.hash === scriptHash);
      if (!script) {
        errorCount++;
        continue;
      }

      const method = script.has_profile ? 'PUT' : 'POST';
      const body = script.has_profile
        ? { ...script.profile_settings, is_public: true }
        : {
            is_public: true,
            disclosure_level: 'full',
            logline: script.logline,
            genres: script.genres,
            custom_usp: script.usp,
            allow_comments: true,
            allow_ratings: true,
            allow_connection_requests: true,
            is_featured: false,
          };

      try {
        const response = await fetch(`${urls.scriptUpdate}${scriptHash}`, {
          method,
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
          },
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          console.error(`Failed to add "${script.title}":`, err);
          errorCount++;
        } else {
          successCount++;
        }
      } catch (err) {
        console.error(`Failed to add "${script.title}":`, err);
        errorCount++;
      }
    }

    setLoading(false);

    // Show results
    if (successCount > 0) {
      toast.success(`${successCount} script${successCount > 1 ? 's' : ''} added to your profile`);
    }
    if (errorCount > 0) {
      toast.warn(`${errorCount} script${errorCount > 1 ? 's' : ''} failed to add`);
    }

    // Close modal and let parent handle refresh
    onClose(true); // Pass true to indicate scripts were added
  };

  const handleClose = () => {
    onClose(false); // Pass false to indicate no changes
  };

  if (!isOpen) return null;

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">
              <i className="fa fa-plus-circle me-2"></i>
              Add Scripts to Your Profile
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={handleClose} aria-label="Close"></button>
          </div>

          <div className="modal-body">

            {/* Search Box */}
            <div className="input-group mb-3">
              <span className="input-group-text"><Search size={18} /></span>
              <input
                type="text"
                className="form-control"
                placeholder="Search scripts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {filteredScripts.length > 0 ? (
              <div className="list-group">
                {filteredScripts.map(script => (
                  <div
                    key={script.hash}
                    className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${
                      selectedScripts.includes(script.hash) ? 'bg-success bg-opacity-10 border-success' : ''
                    }`}
                  >
                    <div className="form-check flex-grow-1">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`select-script-${script.hash}`}
                        checked={selectedScripts.includes(script.hash)}
                        onChange={() => toggleScriptSelection(script.hash)}
                      />
                      <label className="form-check-label ms-2 flex-grow-1" htmlFor={`select-script-${script.hash}`}>
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <strong>{script.title}</strong>
                            <span className="d-block text-muted small">
                              {script.logline?.substring(0, 100)}{script.logline?.length > 100 ? '...' : ''}
                            </span>
                          </div>
                          {selectedScripts.includes(script.hash) && (
                            <div className="ms-3">
                              <span className="badge bg-success">
                                <i className="fa fa-check me-1"></i>
                                Will appear on portfolio
                              </span>
                            </div>
                          )}
                        </div>
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                {searchTerm ? (
                  <p>No scripts found matching "{searchTerm}"</p>
                ) : (
                  <p>All your scripts are already on your profile.</p>
                )}
              </div>
            )}

          </div>

          <div className="modal-footer bg-light">
            <div className="flex-grow-1">
              {/* Acknowledgment Checkbox */}
              <div className="form-check mb-3">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="acknowledgePublic"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="acknowledgePublic">
                  <div className="fw-semibold text-dark">
                    I understand these scripts will be visible on my public portfolio
                  </div>
                  <div className="small text-muted">
                    Selected scripts will be discoverable by other writers, producers, agents, and industry professionals who visit your portfolio page.
                  </div>
                </label>
              </div>

              {selectedScripts.length > 0 && acknowledged && (
                <div className="alert alert-success border-success mb-0">
                  <small className="fw-semibold">
                    <i className="fa fa-check-circle me-1"></i>
                    {selectedScripts.length} script{selectedScripts.length > 1 ? 's' : ''} ready to add to your portfolio
                  </small>
                </div>
              )}

              {selectedScripts.length > 0 && !acknowledged && (
                <div className="alert alert-warning border-warning mb-0">
                  <small className="fw-semibold">
                    <i className="fa fa-exclamation-triangle me-1"></i>
                    Please acknowledge that your scripts will be publicly visible
                  </small>
                </div>
              )}
            </div>

            <div className="ms-3 d-flex flex-column align-items-end">
              <div className="mb-2">
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={handleClose}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={addSelectedScripts}
                  disabled={selectedScripts.length === 0 || !acknowledged || loading}
                >
                  <Save size={18} className="me-1" />
                  {loading ? 'Adding...' : `Add ${selectedScripts.length || 0} to Portfolio`}
                </button>
              </div>
              
              {(selectedScripts.length === 0 || !acknowledged) && (
                <small className="text-muted">
                  {selectedScripts.length === 0 ? 'Select scripts to continue' : 'Acknowledgment required'}
                </small>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScriptSelectionModal;
